import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  AddIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  LockIcon,
} from '@chakra-ui/icons';
import { FaEllipsisV } from 'react-icons/fa';
import { errorMessage, formatFileSize } from './estimateApi';
import {
  ATTACHMENT_GROUPS,
  moveTo,
  splitPhotos,
  stepMove,
  toGroups,
  withPhotos,
} from './attachmentOrder';

export { ATTACHMENT_GROUPS };

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const FixedPage = ({ label, detail }) => (
  <HStack
    px={3}
    py={2}
    bg="gray.100"
    borderRadius="md"
    borderLeftWidth="4px"
    borderLeftColor="gray.400"
    spacing={2}
  >
    <LockIcon boxSize={3} color="gray.400" />
    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
      {label}
    </Text>
    <Spacer />
    {detail && (
      <Text fontSize="xs" color="gray.500">
        {detail}
      </Text>
    )}
  </HStack>
);

/**
 * Two columns: saved PDFs on the left; on the right, the document the
 * customer signs, in order. Cover, Pricing and Authorization are locked in
 * place; PDFs and the roof photo pages are moved with the arrows to any
 * spot between them, and PDFs can go to "Email only".
 * Every change is saved immediately.
 */
const EstimateAttachments = ({
  api,
  attachments,
  onChange,
  photoPosition,
  onPhotoPositionChange,
  libraryItems,
  photoPageCount,
  pricingOptionCount,
  isDisabled,
  toast,
}) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // PDFs plus the roof photos block (when there are photo pages), as one
  // list; moves work on this and are split back apart when saved.
  const showPhotos = photoPageCount > 0;
  const combine = list =>
    showPhotos ? withPhotos(list, photoPosition) : list;
  const items = combine(attachments);
  const groups = toGroups(items);
  const locked = isDisabled || busy;

  const fail = (title, error) =>
    toast({
      title,
      description: errorMessage(error),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });

  // Saves a new order of the combined list (PDFs + photos block).
  const arrange = async (nextItems, previous = attachments) => {
    const previousPosition = photoPosition;
    const { attachments: next, photoPosition: nextPosition } =
      splitPhotos(nextItems);
    onChange(next);
    if (nextPosition) onPhotoPositionChange(nextPosition);
    setBusy(true);
    try {
      const result = await api.post('/api/pdf/attachments/arrange', {
        attachments: next.map(({ key, placement }) => ({ key, placement })),
        ...(nextPosition ? { photoPosition: nextPosition } : {}),
      });
      onChange(result.attachments);
      if (result.photoPosition) onPhotoPositionChange(result.photoPosition);
    } catch (error) {
      onChange(previous);
      onPhotoPositionChange(previousPosition);
      fail('Could not save the order', error);
    }
    setBusy(false);
  };

  // Adds a library PDF or an uploaded file at a spot in the document. The
  // server appends it to its group, then it is moved to the exact spot.
  const addAt = async (request, placement, index, failTitle) => {
    setBusy(true);
    try {
      const { attachments: added } = await request();
      onChange(added);
      const newest = added[added.length - 1];
      const combined = combine(added);
      const zone = combined.filter(a => a.placement === placement);
      if (newest && zone.indexOf(combined.find(a => a.key === newest.key)) !== index) {
        setBusy(false);
        await arrange(moveTo(combined, newest.key, placement, index), added);
        return;
      }
    } catch (error) {
      fail(failTitle, error);
    }
    setBusy(false);
  };

  const addLibraryItem = (id, placement, index) =>
    addAt(
      () => api.post('/api/pdf/library/use', { id, placement }),
      placement,
      index,
      'Could not add the PDF'
    );

  const uploadFile = (file, placement, index) => {
    if (file.type !== 'application/pdf' || file.size > MAX_UPLOAD_BYTES) {
      toast({
        title: `"${file.name}" can't be added`,
        description: 'Use a PDF under 25 MB.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const form = new FormData();
    form.append('file', file);
    form.append('placement', placement);
    form.append('username', api.username);
    addAt(
      () => api.post('/api/pdf/attachments', form),
      placement,
      index,
      'Upload failed'
    );
  };

  const remove = async attachment => {
    setBusy(true);
    try {
      const result = await api.post('/api/pdf/attachments/remove', {
        key: attachment.key,
      });
      onChange(result.attachments);
      if (result.photoPosition) onPhotoPositionChange(result.photoPosition);
    } catch (error) {
      fail('Could not remove the PDF', error);
    }
    setBusy(false);
  };

  const row = attachment => {
    const isEmailOnly = attachment.placement === 'emailOnly';
    const isPhotos = !!attachment.isPhotos;
    const canUp = !!stepMove(items, attachment, -1);
    const canDown = !!stepMove(items, attachment, 1);
    return (
      <HStack
        key={attachment.key}
        px={2}
        py={1.5}
        bg={isPhotos ? 'teal.50' : 'white'}
        borderWidth="1px"
        borderColor={isPhotos ? 'teal.100' : 'gray.200'}
        borderLeftWidth="4px"
        borderLeftColor={
          isPhotos ? 'teal.400' : isEmailOnly ? 'purple.300' : 'blue.400'
        }
        borderRadius="md"
        spacing={2}
      >
        <Box flex={1} minW={0}>
          <Text
            fontSize="sm"
            fontWeight={isPhotos ? 'semibold' : 'normal'}
            color={isPhotos ? 'teal.800' : 'inherit'}
            noOfLines={1}
            title={isPhotos ? undefined : attachment.name}
          >
            {isPhotos ? 'Photos' : attachment.name}
          </Text>
        </Box>
        <Text fontSize="xs" color="gray.400" flexShrink={0}>
          {isPhotos
            ? `${photoPageCount} page${photoPageCount === 1 ? '' : 's'}`
            : formatFileSize(attachment.size)}
        </Text>
        <IconButton
          size="xs"
          variant="ghost"
          icon={<ArrowUpIcon />}
          aria-label={`Move ${attachment.name} up`}
          onClick={() => arrange(stepMove(items, attachment, -1))}
          isDisabled={locked || !canUp}
        />
        <IconButton
          size="xs"
          variant="ghost"
          icon={<ArrowDownIcon />}
          aria-label={`Move ${attachment.name} down`}
          onClick={() => arrange(stepMove(items, attachment, 1))}
          isDisabled={locked || !canDown}
        />
        {isPhotos ? (
          <Box w="24px" flexShrink={0} />
        ) : (
          <Menu placement="bottom-end">
            <MenuButton
              as={IconButton}
              size="xs"
              variant="ghost"
              icon={<FaEllipsisV />}
              aria-label={`More for ${attachment.name}`}
              isDisabled={locked}
            />
            <MenuList fontSize="sm">
              {isEmailOnly ? (
                <MenuItem
                  onClick={() =>
                    arrange(moveTo(items, attachment.key, 'end', groups.end.length))
                  }
                >
                  Include in document
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={() =>
                    arrange(
                      moveTo(
                        items,
                        attachment.key,
                        'emailOnly',
                        groups.emailOnly.length
                      )
                    )
                  }
                >
                  Make email attachment
                </MenuItem>
              )}
              <MenuItem color="red.600" onClick={() => remove(attachment)}>
                Remove
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </HStack>
    );
  };

  const zone = placement => groups[placement].map(row);

  return (
    <Grid
      templateColumns={{ base: '1fr', md: 'minmax(0, 2fr) minmax(0, 3fr)' }}
      gap={5}
    >
      <Box>
        <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>
          PDF LIBRARY
        </Text>
        {libraryItems.length === 0 ? (
          <Text fontSize="xs" color="gray.400">
            The PDF library is empty.
          </Text>
        ) : (
          <VStack align="stretch" spacing={1.5}>
            {libraryItems.map(item => (
              <HStack
                key={item.id}
                px={2}
                py={1.5}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="white"
                spacing={2}
              >
                <Text fontSize="sm" flex={1} minW={0} noOfLines={1} title={item.name}>
                  {item.name}
                </Text>
                <IconButton
                  size="xs"
                  variant="ghost"
                  colorScheme="blue"
                  icon={<AddIcon boxSize={2.5} />}
                  aria-label={`Add ${item.name} to the end of the document`}
                  onClick={() =>
                    addLibraryItem(item.id, 'end', groups.end.length)
                  }
                  isDisabled={locked}
                />
              </HStack>
            ))}
          </VStack>
        )}
      </Box>

      <Box>
        <HStack justify="space-between" mb={2}>
          <Text fontSize="xs" fontWeight="bold" color="gray.600">
            DOCUMENT
          </Text>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={e => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) uploadFile(file, 'end', groups.end.length);
            }}
            style={{ display: 'none' }}
          />
          <Button
            size="xs"
            variant="outline"
            leftIcon={<AddIcon boxSize={2} />}
            onClick={() => inputRef.current.click()}
            isLoading={busy}
            isDisabled={isDisabled}
          >
            Upload PDF
          </Button>
        </HStack>

        <VStack align="stretch" spacing={1.5}>
          <FixedPage label="Cover" />
          {zone('beforePricing')}
          <FixedPage
            label="Pricing"
            detail={`${pricingOptionCount || 0} option${
              pricingOptionCount === 1 ? '' : 's'
            }`}
          />
          {zone('afterPricing')}
          <FixedPage label="Authorization" />
          {zone('end')}
        </VStack>

        {groups.emailOnly.length > 0 && (
          <Box mt={4} pt={3} borderTopWidth="1px">
            <Text
              fontSize="xs"
              fontWeight="bold"
              color="gray.600"
              mb={2}
              textAlign="left"
            >
              EMAIL
            </Text>
            <VStack align="stretch" spacing={1.5}>
              {zone('emailOnly')}
            </VStack>
          </Box>
        )}
      </Box>
    </Grid>
  );
};

export default EstimateAttachments;
